/**
 * Geometry and state rules for the shaped blocks: slabs, stairs, fences, fence
 * gates, walls, panes (glass panes, iron bars) and trapdoors.
 *
 * Every state is its own block id (like the chest facings and door states in
 * blocks.ts), so collision stays a static per-id table. This file is pure data
 * and logic (no registry imports) so blocks.ts, the mesher's models.ts and the
 * smoke tests can all share it.
 *
 * Conventions: boxes are 1/16 block units [x0,y0,z0,x1,y1,z1]; facings are in
 * the chest order 0 = +Z, 1 = +X, 2 = -Z, 3 = -X (so +1 is counter-clockwise
 * seen from above); connection masks are bit0 +X, bit1 -X, bit2 +Z, bit3 -Z.
 */

export type Box = readonly [number, number, number, number, number, number];
export type ShapeKind = 'slab' | 'stair' | 'fence' | 'gate' | 'wall' | 'pane' | 'trapdoor';

export interface ShapeState {
  kind: ShapeKind;
  /** Index into SHAPE_FAMILIES. */
  fam: number;
  /** slab: 0 bottom, 1 top, 2 double; stair / trapdoor: 1 = upside down / top. */
  half: number;
  /** stair, gate, trapdoor: 0..3. */
  facing: number;
  /** stair: 0 straight, 1 inner left, 2 inner right, 3 outer left, 4 outer right. */
  shape: number;
  /** fence, wall, pane: connection bits. */
  mask: number;
  /** gate, trapdoor. */
  open: boolean;
}

/** Unit (x, z) of each facing. */
export const FACING_XZ: ReadonlyArray<readonly [number, number]> = [[0, 1], [1, 0], [0, -1], [-1, 0]];
export const ccw = (f: number): number => (f + 1) & 3;
export const cw = (f: number): number => (f + 3) & 3;

/** Connection bit per neighbour direction. */
export const DIR_BITS: ReadonlyArray<readonly [number, number, number]> = [[1, 0, 1], [-1, 0, 2], [0, 1, 4], [0, -1, 8]];

// --- slabs ----------------------------------------------------------------------------------

export const SLAB_BOTTOM: Box = [0, 0, 0, 16, 8, 16];
export const SLAB_TOP: Box = [0, 8, 0, 16, 16, 16];

// --- stairs ---------------------------------------------------------------------------------

/** The half of the cell on the `f` side, as an xz rectangle [x0, z0, x1, z1]. */
function halfRect(f: number): [number, number, number, number] {
  return f === 0 ? [0, 8, 16, 16] : f === 1 ? [8, 0, 16, 16] : f === 2 ? [0, 0, 16, 8] : [0, 0, 8, 16];
}

/** Stair neighbour as seen by the shape resolver (null = not a stair). */
export type StairNb = { facing: number; half: number } | null;

/**
 * Vanilla StairBlock.getStairsShape: `get(dx, dz)` reads the stair state at an
 * offset (same y). Returns 0 straight, 1/2 inner left/right, 3/4 outer left/right.
 */
export function stairShape(facing: number, half: number, get: (dx: number, dz: number) => StairNb): number {
  const [fx, fz] = FACING_XZ[facing];
  const front = get(fx, fz);
  const canTake = (dir: number): boolean => {
    const n = get(FACING_XZ[dir][0], FACING_XZ[dir][1]);
    return !n || n.facing !== facing || n.half !== half;
  };
  if (front && front.half === half && (front.facing & 1) !== (facing & 1) && canTake((front.facing + 2) & 3)) {
    return front.facing === ccw(facing) ? 3 : 4;
  }
  const back = get(-fx, -fz);
  if (back && back.half === half && (back.facing & 1) !== (facing & 1) && canTake(back.facing)) {
    return back.facing === ccw(facing) ? 1 : 2;
  }
  return 0;
}

/** The upper (or, upside down, lower) layer of a stair: xz rectangles of its step. */
export function stairStepRects(facing: number, shape: number): Array<[number, number, number, number]> {
  const back = halfRect(facing);
  if (shape === 0) return [back];
  const side = halfRect(shape === 1 || shape === 3 ? ccw(facing) : cw(facing));
  if (shape >= 3) {
    // outer corner: only where the back half and the side half overlap
    return [[Math.max(back[0], side[0]), Math.max(back[1], side[1]), Math.min(back[2], side[2]), Math.min(back[3], side[3])]];
  }
  // inner corner: back half plus the part of the side half in front of it
  const rest = halfRect((facing + 2) & 3);
  return [back, [Math.max(rest[0], side[0]), Math.max(rest[1], side[1]), Math.min(rest[2], side[2]), Math.min(rest[3], side[3])]];
}

export function stairBoxes(facing: number, half: number, shape: number): Box[] {
  const top = half === 1;
  const out: Box[] = [top ? SLAB_TOP : SLAB_BOTTOM];
  for (const [x0, z0, x1, z1] of stairStepRects(facing, shape)) {
    out.push(top ? [x0, 0, z0, x1, 8, z1] : [x0, 8, z0, x1, 16, z1]);
  }
  return out;
}

// --- fences / walls / panes -----------------------------------------------------------------

export function fenceBoxes(mask: number, tall: boolean): Box[] {
  const h = tall ? 24 : 16;
  const out: Box[] = [[6, 0, 6, 10, h, 10]];
  if (tall) {
    if (mask & 1) out.push([10, 0, 6, 16, h, 10]);
    if (mask & 2) out.push([0, 0, 6, 6, h, 10]);
    if (mask & 4) out.push([6, 0, 10, 10, h, 16]);
    if (mask & 8) out.push([6, 0, 0, 10, h, 6]);
    return out;
  }
  // vanilla fence arms: two 2px bars at y 6..9 and 12..15
  for (const [y0, y1] of [[6, 9], [12, 15]]) {
    if (mask & 1) out.push([10, y0, 7, 16, y1, 9]);
    if (mask & 2) out.push([0, y0, 7, 6, y1, 9]);
    if (mask & 4) out.push([7, y0, 10, 9, y1, 16]);
    if (mask & 8) out.push([7, y0, 0, 9, y1, 6]);
  }
  return out;
}

/** A wall's centre post is dropped on a straight run (vanilla `up`). */
export const wallHasPost = (mask: number): boolean => mask !== 3 && mask !== 12;

export function wallBoxes(mask: number, tall: boolean): Box[] {
  const h = tall ? 24 : 16, lh = tall ? 24 : 14;
  const out: Box[] = [];
  if (wallHasPost(mask)) out.push([4, 0, 4, 12, h, 12]);
  else if (mask === 3) out.push([0, 0, 5, 16, lh, 11]);
  else out.push([5, 0, 0, 11, lh, 16]);
  if (wallHasPost(mask)) {
    if (mask & 1) out.push([12, 0, 5, 16, lh, 11]);
    if (mask & 2) out.push([0, 0, 5, 4, lh, 11]);
    if (mask & 4) out.push([5, 0, 12, 11, lh, 16]);
    if (mask & 8) out.push([5, 0, 0, 11, lh, 4]);
  }
  return out;
}

/** Pane / bars: a 2px post and a 2px arm per connection; a lone pane is a plus. */
export function paneBoxes(mask: number): Box[] {
  const out: Box[] = [[7, 0, 7, 9, 16, 9]];
  const any = mask !== 0;
  if (mask & 1 || !any) out.push([9, 0, 7, 16, 16, 9]);
  if (mask & 2 || !any) out.push([0, 0, 7, 7, 16, 9]);
  if (mask & 4 || !any) out.push([7, 0, 9, 9, 16, 16]);
  if (mask & 8 || !any) out.push([7, 0, 0, 9, 16, 7]);
  return out;
}

// --- gates ------------------------------------------------------------------------------------

/** Gates between posts spanning x (facing along z) or z (facing along x). */
export function gateBoxes(facing: number, open: boolean, tall: boolean): Box[] {
  const spanX = (facing & 1) === 0;
  const t = (b: Box): Box => (spanX ? b : [b[2], b[1], b[0], b[5], b[4], b[3]]);
  if (tall) return open ? [] : [t([0, 0, 6, 16, 24, 10])];
  const out: Box[] = [t([0, 5, 7, 2, 16, 9]), t([14, 5, 7, 16, 16, 9])];
  if (!open) {
    out.push(t([2, 6, 7, 14, 9, 9]), t([2, 12, 7, 14, 15, 9]), t([6, 9, 7, 10, 12, 9]));
  } else {
    // leaves swung toward the facing side, each hanging off its post
    const near = facing === 0 || facing === 1;
    const [z0, z1] = near ? [9, 16] : [0, 7];
    for (const [x0, x1] of [[0, 2], [14, 16]]) {
      out.push(t([x0, 6, z0, x1, 9, z1]), t([x0, 12, z0, x1, 15, z1]));
    }
  }
  return out;
}

// --- trapdoors --------------------------------------------------------------------------------

export function trapdoorBox(facing: number, top: boolean, open: boolean): Box {
  if (!open) return top ? [0, 13, 0, 16, 16, 16] : [0, 0, 0, 16, 3, 16];
  // the open panel stands on the side opposite its facing (vanilla *_OPEN_AABB)
  return facing === 0 ? [0, 0, 0, 16, 16, 3] : facing === 1 ? [0, 0, 0, 3, 16, 16]
    : facing === 2 ? [0, 0, 13, 16, 16, 16] : [13, 0, 0, 16, 16, 16];
}

// --- per-state boxes ---------------------------------------------------------------------------

/** Collision boxes of a state (fences, walls and closed gates are 1.5 tall). */
export function stateCollision(s: ShapeState): Box[] {
  switch (s.kind) {
    case 'slab': return [s.half === 0 ? SLAB_BOTTOM : s.half === 1 ? SLAB_TOP : [0, 0, 0, 16, 16, 16]];
    case 'stair': return stairBoxes(s.facing, s.half, s.shape);
    case 'fence': return fenceBoxes(s.mask, true);
    case 'wall': return wallBoxes(s.mask, true);
    case 'pane': return paneBoxes(s.mask);
    case 'gate': return gateBoxes(s.facing, s.open, true);
    case 'trapdoor': return [trapdoorBox(s.facing, s.half === 1, s.open)];
  }
}

/** Visible boxes of a state. */
export function stateVisual(s: ShapeState): Box[] {
  switch (s.kind) {
    case 'fence': return fenceBoxes(s.mask, false);
    case 'wall': return wallBoxes(s.mask, false);
    case 'gate': return gateBoxes(s.facing, s.open, false);
    default: return stateCollision(s);
  }
}

// --- plant jitter -------------------------------------------------------------------------------

/** Cheap deterministic 0..1 hash of a column. */
export function jitter(x: number, z: number, salt: number): number {
  let h = Math.imul(x, 0x1f1f1f1f) ^ Math.imul(z, 0x5bd1e995) ^ salt;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 8) / 0x1000000;
}

/**
 * Vanilla nudges plants off the block centre so fields don't look gridded: the offset (in blocks) of
 * the plant in world column (wx, wz). Shared by the mesher and the selection box, which moves with it.
 */
export const plantJitterX = (wx: number, wz: number): number => (jitter(wx, wz, 0x51) - 0.5) * 0.4;
export const plantJitterZ = (wx: number, wz: number): number => (jitter(wx, wz, 0xa7) - 0.5) * 0.4;
